import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronUp, Mail, ArrowRight, Share2 } from "lucide-react";
import { Card, Pill } from "../common/Primitives";
import DigestShareCard from "./DigestShareCard";
import api from "../../lib/api";

/** Founder-dashboard inline card. Shows how their startup appears in an
 *  investor's weekly digest. Collapsed by default; toggle to expand the
 *  rendered HTML preview inline. Full view lives at /startup/investor-digest-preview. */
export default function InvestorDigestPreviewCard() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  const load = async () => {
    try {
      const { data: d } = await api.get("/startup/investor-digest-preview");
      setData(d);
      setLoaded(true);
    } catch {
      setLoaded(true);
    }
  };

  useEffect(() => { load(); }, []);

  if (!loaded) return null;
  if (!data) return null;

  const delta = data.trust_delta_7d || 0;
  const deltaEl = (
    <span
      className={`font-mono text-sm ${delta > 0 ? "text-emerald-700" : delta < 0 ? "text-rose-700" : "text-ink-soft"}`}
      data-testid="digest-inline-delta"
    >
      {delta > 0 ? "▲" : delta < 0 ? "▼" : "—"} {Math.abs(delta)}
    </span>
  );

  return (
    <Card testId="digest-inline-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Mail size={14} className="text-gold" />
          <div>
            <div className="font-heading text-ink">How investors see you this week</div>
            <div className="text-xs text-ink-soft mt-0.5">
              Trust <strong className="text-ink">{data.trust_score}/100</strong> {deltaEl} · <strong className="text-ink">{data.watchers}</strong> investors watching
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShareOpen((v) => !v)}
            className="text-xs font-mono uppercase tracking-widest text-[#8A6600] hover:underline flex items-center gap-1"
            data-testid="digest-inline-share-toggle"
          >
            <Share2 size={12} /> {shareOpen ? "Hide share" : "Share"}
          </button>
          <button
            onClick={() => setOpen((v) => !v)}
            className="text-xs font-mono uppercase tracking-widest text-[#8A6600] hover:underline flex items-center gap-1"
            data-testid="digest-inline-toggle"
          >
            {open ? <>Hide preview <ChevronUp size={12} /></> : <>Show what investors see <ChevronDown size={12} /></>}
          </button>
          <Link
            to="/startup/investor-digest-preview"
            className="text-xs font-mono uppercase tracking-widest text-ink-soft hover:text-gold flex items-center gap-1"
            data-testid="digest-inline-fullview"
          >
            Full view <ArrowRight size={12} />
          </Link>
        </div>
      </div>

      {data.verified_pills.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {data.verified_pills.map((p) => (<Pill key={p} tone="success">{p}</Pill>))}
        </div>
      )}

      {shareOpen && (
        <div className="mt-4" data-testid="digest-inline-share">
          <DigestShareCard data={data} />
        </div>
      )}

      {open && (
        <div className="mt-4 border border-navy/15 rounded overflow-hidden" data-testid="digest-inline-html">
          <iframe
            title="Investor Digest Preview inline"
            srcDoc={data.html_preview}
            className="w-full min-h-[420px] border-0 bg-white"
          />
        </div>
      )}
    </Card>
  );
}
